import { ReactNode, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { twMerge } from 'tailwind-merge';

import { GlassBox } from 'components/glassBox/GlassBox';
import { useIsMobile } from 'hooks/useScreenSize';

const DEFAULT_TOOLTIP_WIDTH = 162;

type TooltipProps = {
  children: ReactNode;
  content: ReactNode;
  className?: string;
  contentClassName?: string;
  position?: 'top' | 'bottom' | 'left' | 'right';
  width?: number;
  offsetY?: number;
};
const TOOLTIP_GAP = 8;
const ARROW_WIDTH = 17;
const ARROW_HEIGHT = 8;
const ARROW_COLOR = '#243D91';
const EDGE_PADDING = 8;

type ArrowPosition = 'top' | 'bottom' | 'left' | 'right';

const arrowRotation: Record<ArrowPosition, number> = {
  top: 180,
  bottom: 0,
  left: 90,
  right: -90,
};

const TooltipArrow = ({ position }: { position: ArrowPosition }) => (
  <svg
    width={ARROW_WIDTH}
    height={ARROW_HEIGHT}
    viewBox={`0 0 ${ARROW_WIDTH} ${ARROW_HEIGHT}`}
    style={{ display: 'block', transform: `rotate(${arrowRotation[position]}deg)` }}
  >
    <polygon points={`0,${ARROW_HEIGHT} ${ARROW_WIDTH / 2},0 ${ARROW_WIDTH},${ARROW_HEIGHT}`} fill={ARROW_COLOR} />
  </svg>
);

export const Tooltip = ({
  children,
  content,
  className,
  contentClassName,
  position = 'top',
  width = DEFAULT_TOOLTIP_WIDTH,
  offsetY = 0,
}: TooltipProps) => {
  const [isVisible, setIsVisible] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  const [arrowShiftX, setArrowShiftX] = useState(0);
  const [triggerRect, setTriggerRect] = useState<DOMRect | null>(null);
  const triggerRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const isMobile = useIsMobile();

  const hideTooltip = useCallback(() => {
    setIsVisible(false);
    setTriggerRect(null);
  }, []);

  useEffect(() => {
    if (!isVisible) return;

    window.addEventListener('scroll', hideTooltip, true);
    return () => {
      window.removeEventListener('scroll', hideTooltip, true);
    };
  }, [isVisible, hideTooltip]);

  useEffect(() => {
    if (!isMobile || !isVisible) return;

    const handleClickOutside = (e: MouseEvent) => {
      if (
        triggerRef.current &&
        !triggerRef.current.contains(e.target as Node) &&
        tooltipRef.current &&
        !tooltipRef.current.contains(e.target as Node)
      ) {
        hideTooltip();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isMobile, isVisible, hideTooltip]);

  // Calculate position after tooltip is rendered and we know its actual height
  useLayoutEffect(() => {
    if (!isVisible || !triggerRect || !tooltipRef.current) return;

    const tooltipHeight = tooltipRef.current.offsetHeight;

    let top = 0;
    let left = 0;

    switch (position) {
      case 'top':
        top = triggerRect.top - tooltipHeight - TOOLTIP_GAP + window.scrollY + offsetY;
        left = triggerRect.left + triggerRect.width / 2 - width / 2 + window.scrollX;
        break;
      case 'bottom':
        top = triggerRect.bottom + TOOLTIP_GAP + window.scrollY + offsetY;
        left = triggerRect.left + triggerRect.width / 2 - width / 2 + window.scrollX;
        break;
      case 'left':
        top = triggerRect.top + triggerRect.height / 2 - tooltipHeight / 2 + window.scrollY + offsetY;
        left = triggerRect.left - width - TOOLTIP_GAP + window.scrollX;
        break;
      case 'right':
        top = triggerRect.top + triggerRect.height / 2 - tooltipHeight / 2 + window.scrollY + offsetY;
        left = triggerRect.right + TOOLTIP_GAP + window.scrollX;
        break;
    }

    let shiftX = 0;
    if (isMobile && (position === 'top' || position === 'bottom')) {
      const minLeft = EDGE_PADDING;
      const maxLeft = window.innerWidth - width - EDGE_PADDING;
      const clampedLeft = Math.max(minLeft, Math.min(left, maxLeft));
      shiftX = left - clampedLeft;
      left = clampedLeft;
    }

    setArrowShiftX(shiftX);
    setTooltipPosition({ top, left });
  }, [isVisible, triggerRect, position, width, offsetY, isMobile]);

  const updateTriggerRect = useCallback(() => {
    if (triggerRef.current) {
      setTriggerRect(triggerRef.current.getBoundingClientRect());
    }
  }, []);

  const handleMouseEnter = () => {
    if (!isMobile) {
      updateTriggerRect();
      setIsVisible(true);
    }
  };

  const handleMouseLeave = () => {
    if (!isMobile) {
      setIsVisible(false);
    }
  };

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (isMobile) {
      if (isVisible) {
        hideTooltip();
      } else {
        updateTriggerRect();
        setIsVisible(true);
      }
    }
  };

  return (
    <>
      <div
        ref={triggerRef}
        className={twMerge('tw-inline-flex', className)}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onClick={handleClick}
      >
        {children}
      </div>
      {createPortal(
        <div
          ref={tooltipRef}
          className={twMerge(
            'tw-fixed tw-z-[9999] tw-transition-opacity tw-duration-200',
            isMobile ? 'tw-pointer-events-auto' : 'tw-pointer-events-none',
            isVisible ? 'tw-visible tw-opacity-100' : 'tw-invisible tw-opacity-0'
          )}
          style={{
            top: tooltipPosition.top,
            left: tooltipPosition.left,
          }}
          onClick={(e) => {
            if (isMobile) {
              e.stopPropagation();
            }
          }}
        >
          <div className="tw-relative" style={{ width }}>
            <GlassBox
              gradient="linear-gradient(to bottom right, #2D5EFF 0%, rgba(45, 94, 255, 0.2) 100%)"
              borderRadius="5px"
              className={twMerge(
                'tw-flex tw-items-center tw-justify-center tw-p-[10px] tw-text-center tw-font-jost tw-text-[12px] tw-font-normal tw-leading-[15px] tw-text-white',
                contentClassName
              )}
            >
              {content}
            </GlassBox>
            <div
              style={{
                position: 'absolute',
                ...(position === 'top' && {
                  bottom: -ARROW_HEIGHT,
                  left: `calc(50% + ${arrowShiftX}px)`,
                  transform: 'translateX(-50%)',
                }),
                ...(position === 'bottom' && {
                  top: -ARROW_HEIGHT,
                  left: `calc(50% + ${arrowShiftX}px)`,
                  transform: 'translateX(-50%)',
                }),
                ...(position === 'left' && {
                  right: -ARROW_HEIGHT,
                  top: '50%',
                  transform: 'translateY(-50%)',
                }),
                ...(position === 'right' && {
                  left: -ARROW_HEIGHT,
                  top: '50%',
                  transform: 'translateY(-50%)',
                }),
              }}
            >
              <TooltipArrow position={position} />
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
};
